import { accountsClient, isAccountsRouteConfigured } from '../lib/trpc/accounts';
import { primeTokenCache } from './auth';

export function getMembershipOrganization(membership) {
  return membership?.organization || membership?.publicOrganizationData || null;
}

export function getMembershipOrganizationId(membership) {
  return getMembershipOrganization(membership)?.id || '';
}

export function getOrganizationDisplayName(organization) {
  return organization?.name || organization?.slug || organization?.id || 'Organization';
}

export function getMembershipDisplayName(membership) {
  return getOrganizationDisplayName(getMembershipOrganization(membership));
}

/**
 * Move the dashboard into a new Clerk organization context.
 *
 * All dashboard entry points (the workspace menu, the no-active-org picker,
 * and post-provisioning onboarding) use this sequence. Existing-workspace
 * switches redirect through Clerk so it owns both the context change and the
 * subsequent document navigation.
 */
export async function activateDashboardOrganization({
  clerk,
  setActive,
  clerkOrgId,
  syncContext = 'Syncing active organization',
  redirectUrl,
}) {
  if (!clerkOrgId) {
    throw new Error('An organization is required to continue.');
  }

  // Clerk documents useOrganizationList().setActive for custom organization
  // switchers. Use it when available because it is paired with the memberships
  // rendered in the dashboard; the Clerk instance is required for onboarding,
  // where no organization list is mounted yet.
  const activate = setActive || clerk?.setActive?.bind(clerk);
  if (!activate) {
    throw new Error('Organization switching is not ready yet.');
  }

  await activate({
    organization: clerkOrgId,
    ...(redirectUrl ? { redirectUrl } : {}),
  });

  // Clerk owns the next document when a redirect URL is supplied. Starting a
  // token or active-org sync request here races page unload and can surface a
  // misleading failure toast even though the organization switch succeeded.
  // ClerkAuthWrapper performs the normal token priming and sync after reload.
  if (redirectUrl) {
    return {
      freshToken: null,
      syncResult: {
        ok: true,
        skipped: true,
      },
    };
  }

  const freshToken = clerk?.session
    ? await clerk.session.getToken({
        skipCache: true,
        organizationId: clerkOrgId,
      })
    : null;

  if (!freshToken) {
    throw new Error('Could not establish an organization-scoped session. Please try again.');
  }
  primeTokenCache(freshToken);

  let syncResult;
  try {
    syncResult = await syncActiveOrganization({
      token: freshToken,
      context: syncContext,
    });
  } catch (error) {
    // Activation is already complete and the JWT has the selected-org claim.
    // Treat persistence as best-effort rather than rolling the user back into a
    // stale workspace after a transient network failure.
    syncResult = {
      ok: false,
      status: 0,
      error: error?.message || 'Unable to persist active organization',
    };
  }

  return { freshToken, syncResult };
}

export async function syncActiveOrganization({ token, context = 'Syncing active organization' }) {
  if (!isAccountsRouteConfigured('POST /users/me/active-org/sync') || !token) {
    return {
      ok: false,
      status: 0,
      error: 'Missing accounts backend URL or auth token',
    };
  }

  return accountsClient.requestJson(
    'POST /users/me/active-org/sync',
    { authorization: token, input: {} },
    {
      context,
      retries: 1,
      retryDelay: 500,
    },
  );
}
